import React from "react";
import { ARCH_LAYERS } from "@/data/synthetic";

/*
Animated AWS architecture diagram.
Renders 6 horizontal layers with nodes, and animated connection lines with dashed pulses
travelling between adjacent layers. Pure SVG + CSS animation (no motion lib required).
*/

const LAYER_META = [
  { color: "#00E5FF", accent: "cyan-300" },
  { color: "#0052FF", accent: "blue-300" },
  { color: "#7C3AED", accent: "violet-300" },
  { color: "#0052FF", accent: "blue-300" },
  { color: "#00E5FF", accent: "cyan-300" },
  { color: "#22C55E", accent: "emerald-300" },
];

export default function ArchitectureDiagram() {
  return (
    <div className="glass rounded-2xl p-6 lg:p-8 relative overflow-hidden" data-testid="arch-diagram">
      {/* Backdrop grid */}
      <div className="absolute inset-0 bg-grid-fine opacity-30 pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between mb-6">
          <div className="tech-label">SYSTEM ARCHITECTURE · MULTI-TENANT · US-EAST-1 · CA-CENTRAL-1</div>
          <div className="flex items-center gap-2 text-[10px] font-mono text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-ring inline-block" />
            LIVE · CONTROL PLANE
          </div>
        </div>

        <div className="space-y-2">
          {ARCH_LAYERS.map((layer, li) => {
            const meta = LAYER_META[li] || LAYER_META[0];
            return (
              <div key={layer.layer} className="relative">
                {/* Vertical connector to previous layer */}
                {li > 0 && (
                  <svg className="absolute left-0 right-0 -top-8 h-8 pointer-events-none" viewBox="0 0 1000 32" preserveAspectRatio="none" style={{ width: "100%", height: 32 }}>
                    {[...Array(6)].map((_, i) => {
                      const x = 120 + i * 145;
                      return (
                        <g key={i}>
                          <line x1={x} y1="0" x2={x} y2="32" stroke={meta.color} strokeOpacity="0.25" strokeWidth="1" />
                          <line x1={x} y1="0" x2={x} y2="32" stroke={meta.color} strokeWidth="1.5" strokeDasharray="3 5" className="animate-dash" style={{ animationDelay: `${i * 0.2}s` }} />
                        </g>
                      );
                    })}
                  </svg>
                )}

                <div className="rounded-xl border relative overflow-hidden" style={{ borderColor: `${meta.color}22`, background: `linear-gradient(90deg, ${meta.color}0A, transparent 60%)` }}>
                  {/* Left rail */}
                  <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: meta.color, opacity: 0.7 }} />
                  <div className="pl-5 pr-4 py-4 flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-56 flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-slate-500">L{li + 1}</span>
                        <span className="font-mono text-[10px]" style={{ color: meta.color }}>▸</span>
                      </div>
                      <div className="font-heading text-white mt-1 text-sm tracking-tight">{layer.layer}</div>
                    </div>
                    <div className="flex-1 flex flex-wrap gap-2">
                      {layer.nodes.map((n, ni) => (
                        <div key={n} className="group relative rounded-md border px-3 py-2 text-xs font-mono text-slate-100 bg-black/40 transition-transform hover:-translate-y-0.5" style={{ borderColor: `${meta.color}33` }}>
                          <span className="opacity-70 mr-1.5" style={{ color: meta.color }}>■</span>
                          {n}
                          <span className="ml-2 text-[9px] text-slate-500">S{li + 1}.{`${ni + 1}`.padStart(2, "0")}</span>
                        </div>
                      ))}
                    </div>
                    <div className="hidden md:flex items-center gap-2 flex-shrink-0">
                      <div className="w-2 h-2 rounded-full pulse-ring" style={{ background: meta.color }} />
                      <span className="tech-label text-[9px]">HEALTHY</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer legend */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-white/5">
          <div className="flex items-center gap-4 text-[10px] font-mono text-slate-400">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-cyan-400 inline-block" /> EDGE</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> APPLICATION</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet-500 inline-block" /> COMPUTE</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> OBSERVABILITY</span>
          </div>
          <div className="text-[10px] font-mono text-slate-500">TENANCY: ROW-LEVEL · TLS 1.3 · IAM-SCOPED · VPC-ISOLATED</div>
        </div>
      </div>
    </div>
  );
}
